import { useState } from 'react';
import { Button, Card, Badge, Spinner, Row, Col, Accordion, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ChevronDown, ChevronRight, Bot, Cpu, Plus, Eye, Edit2, Trash2, Copy, Check } from 'lucide-react';
import { useAgentRegistry } from '../../../apps/hooks/agent/useAgentRegistry';
import type { AgentPersonaResponse, RegistryTreeResponse } from '../../../infrastructure/AIAgentRegistryClient';
import { AgentDetailModal } from './AgentDetailModal';
import { UpsertAgentModal } from './UpsertAgentModal';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { formatDate } from '../../../utils/dateUtils';

const CopyableId = ({ id }: { id: string }) => {
  const [copied, setCopied] = useState(false);
  const shortId = id.substring(0, 8);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <OverlayTrigger placement="top" overlay={<Tooltip>{copied ? 'Copied!' : `Click to copy: ${id}`}</Tooltip>}>
      <Badge
        bg="light"
        text="dark"
        className="ms-2 d-inline-flex align-items-center"
        style={{ cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.7rem' }}
        onClick={handleCopy}
      >
        {shortId}...
        {copied ? <Check size={10} className="ms-1 text-success" /> : <Copy size={10} className="ms-1" />}
      </Badge>
    </OverlayTrigger>
  );
};

interface AgentPersonaTabProps {
  registryTree: RegistryTreeResponse | undefined;
}

export const AgentPersonaTab = ({ registryTree }: AgentPersonaTabProps) => {
  const { supervisorsQuery, useMicroAgentsQuery, upsertAgentMutation, deleteAgentMutation } = useAgentRegistry();
  const [expandedSupervisors, setExpandedSupervisors] = useState<Set<string>>(new Set());
  const [selectedAgent, setSelectedAgent] = useState<AgentPersonaResponse | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showUpsertModal, setShowUpsertModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState<AgentPersonaResponse | null>(null);
  const [creatingType, setCreatingType] = useState<'supervisor' | 'micro'>('supervisor');
  const [parentSupervisorId, setParentSupervisorId] = useState<string | undefined>();

  const { data: supervisors = [], isLoading } = supervisorsQuery;

  const toggleSupervisor = (id: string) => {
    setExpandedSupervisors((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const handleViewAgent = (agent: AgentPersonaResponse) => {
    setSelectedAgent(agent);
    setShowDetailModal(true);
  };

  const handleCreateSupervisor = () => {
    setEditingAgent(null);
    setCreatingType('supervisor');
    setParentSupervisorId(undefined);
    setShowUpsertModal(true);
  };

  const handleCreateMicroAgent = (supervisorId: string) => {
    setEditingAgent(null);
    setCreatingType('micro');
    setParentSupervisorId(supervisorId);
    setShowUpsertModal(true);
  };

  const handleEditAgent = (agent: AgentPersonaResponse) => {
    setEditingAgent(agent);
    setCreatingType(agent.type);
    setParentSupervisorId(agent.parentId);
    setShowUpsertModal(true);
  };

  const handleDeleteAgent = (agent: AgentPersonaResponse) => {
    setSelectedAgent(agent);
    setShowDeleteModal(true);
  };

  if (isLoading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">
          <Bot className="me-2" size={20} />
          Agent Personas
        </h5>
        <Button variant="primary" size="sm" onClick={handleCreateSupervisor}>
          <Plus size={16} className="me-1" /> New Supervisor
        </Button>
      </div>

      {supervisors.length === 0 ? (
        <Card body className="text-center text-muted">
          No supervisor agents found. Create your first supervisor to get started.
        </Card>
      ) : (
        <Accordion>
          {supervisors.map((supervisor) => (
            <SupervisorCard
              key={supervisor.id}
              supervisor={supervisor}
              isExpanded={expandedSupervisors.has(supervisor.id)}
              onToggle={() => toggleSupervisor(supervisor.id)}
              onView={() => handleViewAgent(supervisor)}
              onEdit={() => handleEditAgent(supervisor)}
              onDelete={() => handleDeleteAgent(supervisor)}
              onAddMicro={() => handleCreateMicroAgent(supervisor.id)}
              useMicroAgentsQuery={useMicroAgentsQuery}
              onViewMicro={handleViewAgent}
              onEditMicro={handleEditAgent}
              onDeleteMicro={handleDeleteAgent}
            />
          ))}
        </Accordion>
      )}

      <AgentDetailModal
        show={showDetailModal}
        agent={selectedAgent}
        onHide={() => setShowDetailModal(false)}
        onEdit={() => {
          setShowDetailModal(false);
          if (selectedAgent) handleEditAgent(selectedAgent);
        }}
      />

      <UpsertAgentModal
        show={showUpsertModal}
        agent={editingAgent}
        type={creatingType}
        parentId={parentSupervisorId}
        registryTree={registryTree}
        onHide={() => setShowUpsertModal(false)}
        onSubmit={(data) => {
          upsertAgentMutation.mutate(data);
          setShowUpsertModal(false);
        }}
        isSubmitting={upsertAgentMutation.isPending}
      />

      <DeleteConfirmModal
        show={showDeleteModal}
        title="Delete Agent"
        message={`Are you sure you want to delete "${selectedAgent?.name}"? This action cannot be undone.`}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={() => {
          if (selectedAgent) deleteAgentMutation.mutate(selectedAgent.id);
          setShowDeleteModal(false);
        }}
        isDeleting={deleteAgentMutation.isPending}
      />
    </>
  );
};

interface SupervisorCardProps {
  supervisor: AgentPersonaResponse;
  isExpanded: boolean;
  onToggle: () => void;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onAddMicro: () => void;
  useMicroAgentsQuery: ReturnType<typeof useAgentRegistry>['useMicroAgentsQuery'];
  onViewMicro: (agent: AgentPersonaResponse) => void;
  onEditMicro: (agent: AgentPersonaResponse) => void;
  onDeleteMicro: (agent: AgentPersonaResponse) => void;
}

const SupervisorCard = ({
  supervisor,
  isExpanded,
  onToggle,
  onView,
  onEdit,
  onDelete,
  onAddMicro,
  useMicroAgentsQuery,
  onViewMicro,
  onEditMicro,
  onDeleteMicro,
}: SupervisorCardProps) => {
  const microAgentsQuery = useMicroAgentsQuery(supervisor.id);
  const { data: microAgents = [], isLoading: loadingMicro } = microAgentsQuery;

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex justify-content-between align-items-center" style={{ cursor: 'pointer' }}>
        <div className="d-flex align-items-center flex-grow-1" onClick={onToggle}>
          {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          <Bot className="mx-2 text-primary" size={20} />
          <div>
            <strong>{supervisor.name}</strong>
            <CopyableId id={supervisor.id} />
            <Badge bg="primary" className="ms-2">Supervisor</Badge>
            <Badge bg="secondary" className="ms-2">{microAgents.length} Micro Agents</Badge>
          </div>
        </div>
        <div onClick={(e) => e.stopPropagation()}>
          <Button variant="outline-info" size="sm" className="me-1" onClick={onView}><Eye size={14} /></Button>
          <Button variant="outline-secondary" size="sm" className="me-1" onClick={onEdit}><Edit2 size={14} /></Button>
          <Button variant="outline-danger" size="sm" onClick={onDelete}><Trash2 size={14} /></Button>
        </div>
      </Card.Header>

      {isExpanded && (
        <Card.Body>
          <p className="text-muted mb-3">{supervisor.description}</p>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">
              <Cpu size={16} className="me-2" />
              Micro Agents
            </h6>
            <Button variant="outline-primary" size="sm" onClick={onAddMicro}>
              <Plus size={14} className="me-1" /> Add Micro Agent
            </Button>
          </div>

          {loadingMicro ? (
            <Spinner animation="border" size="sm" />
          ) : microAgents.length === 0 ? (
            <p className="text-muted small">No micro agents under this supervisor.</p>
          ) : (
            <Row>
              {microAgents.map((micro) => (
                <Col md={6} key={micro.id} className="mb-2">
                  <Card className="h-100">
                    <Card.Body className="py-2 px-3">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <div className="fw-bold">
                            {micro.name}
                            <CopyableId id={micro.id} />
                          </div>
                          <small className="text-muted">{micro.description}</small>
                          {micro.manualId && (
                            <div><Badge bg="info" className="mt-1">Has Manual</Badge></div>
                          )}
                        </div>
                        <div className="d-flex">
                          <Button variant="link" size="sm" className="p-1" onClick={() => onViewMicro(micro)}><Eye size={14} /></Button>
                          <Button variant="link" size="sm" className="p-1" onClick={() => onEditMicro(micro)}><Edit2 size={14} /></Button>
                          <Button variant="link" size="sm" className="p-1 text-danger" onClick={() => onDeleteMicro(micro)}><Trash2 size={14} /></Button>
                        </div>
                      </div>
                      <small className="text-muted d-block mt-1">Updated: {formatDate(micro.updatedAt)}</small>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Card.Body>
      )}
    </Card>
  );
};

